/**
 * Every budget in the design: what a part has, what it is allowed, and whether
 * that has actually been settled.
 *
 * A LONG TABLE, not a wide one. Budgets are sparse - a few quantities on a
 * handful of subsystems - so a column per quantity would be mostly empty while
 * the rows that carry a figure are the entire point. One row per budget also
 * puts the verdicts in a single column, which is the column somebody scans.
 *
 * The three columns that matter are Total, Budget and Verdict, and the reason
 * Margin is a separate column from Verdict rather than folded into it is that
 * ITS ABSENCE IS INFORMATION. `no margin` against a total that is under its
 * budget looks like a gap in the table until you read the verdict beside it, and
 * that is precisely the connection a reader has to make: the number is a floor,
 * so the headroom cannot be computed, and the 58 kg that subtraction would give
 * is not there to be had.
 *
 * Ordered by the tree rather than by severity. Breaches first would read better
 * and would break the one check this table is for - whether the allocations
 * handed out under a subsystem add up to more than the subsystem was given,
 * which needs them adjacent.
 */

import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import BudgetVerdict, { NO_MARGIN_WHY } from './BudgetVerdict'
import { MONOSPACE } from '../../theme'
import type { AllocationRow } from './budget-model'
import { formatTotal } from './budget-model'

export const NO_BUDGETS =
  'No part in this design states a budget. An attribute is budgeted by marking it ' +
  '@EchelonSysML::Allocated in the model with a figure — the library says mass and ' +
  'power are held per subsystem, and each subsystem supplies its own number.'

export default function AllocationTable({
  rows,
  rootName,
}: {
  rows: AllocationRow[]
  rootName: string
}) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        {NO_BUDGETS}
      </Typography>
    )
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={`Budgets within ${rootName}`}>
        <TableHead>
          <TableRow>
            <TableCell>Part</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">Budget</TableCell>
            <TableCell align="right">Margin</TableCell>
            <TableCell>Verdict</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((entry) => (
            <TableRow key={entry.key} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  {/* Indented by depth, so an allocation reads as sitting
                      inside the one above it - which is the whole question when
                      checking whether the portions add up. */}
                  <Box sx={{ pl: `${Math.max(0, entry.row.depth - rows[0].row.depth) * 12}px` }}>
                    {entry.row.name}
                  </Box>
                  {/* The ID only where it ADDS something. A usage often
                      carries its own name as its identifier, and printing
                      'vehicle vehicle' spends a column on a repetition. */}
                  {entry.row.id !== entry.row.name && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: MONOSPACE }}
                    >
                      {entry.row.id}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>{entry.attribute.label}</TableCell>

              {/* The total in its FULL form - the bound and all. A budget check
                  on '≥ 85.05 kg' and one on '85.05 kg' are different claims,
                  and stripping the sign here would hide why the verdict is what
                  it is. */}
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatTotal(entry.total)}
              </TableCell>

              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {entry.reading.budget}
              </TableCell>

              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {entry.reading.hasMargin ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      // The sign is on the number; colour only agrees with it,
                      // so a reader who cannot tell the two apart still has the
                      // '+' and the '-'.
                      color: entry.check.status === 'over' ? 'error.main' : 'success.main',
                      fontWeight: 600,
                    }}
                  >
                    {entry.reading.margin}
                  </Typography>
                ) : (
                  <Tooltip title={NO_MARGIN_WHY} describeChild>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic', textDecoration: 'underline dotted' }}
                    >
                      no margin
                    </Typography>
                  </Tooltip>
                )}
              </TableCell>

              <TableCell>
                {/* The badge alone here: the budget and margin already have
                    their own columns, and repeating them would be three copies
                    of one number on one row. */}
                <BudgetVerdict reading={entry.reading} detailed={false} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
